import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Check, X } from 'lucide-react';
import { useSettings } from '../lib/usePublicData';

const STORAGE_KEY = 'zz_cookie_consent';
const EVENT_NAME = 'zz:consent-changed';

/**
 * Read consent from localStorage. Returns 'accepted', 'declined', or null.
 */
export function readConsent() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === 'accepted' || v === 'declined' ? v : null;
    } catch (e) {
        return null;
    }
}

/**
 * Subscribe to consent changes. Returns an unsubscribe fn.
 * Other components (Analytics) listen to this so they can hot-load scripts.
 */
export function onConsentChange(cb) {
    const handler = (e) => cb(e.detail);
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
}

function setConsent(value) {
    try {
        localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
        /* noop */
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
}

export default function CookieConsent() {
    const { data: settings } = useSettings();
    const enabled = settings?.consent_banner_enabled !== false; // default ON
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!enabled) return;
        // Delay a frame so the banner doesn't compete with hero paint.
        const t = setTimeout(() => {
            if (readConsent() === null) setVisible(true);
        }, 600);
        return () => clearTimeout(t);
    }, [enabled]);

    const accept = useCallback(() => {
        setConsent('accepted');
        setVisible(false);
    }, []);

    const decline = useCallback(() => {
        setConsent('declined');
        setVisible(false);
    }, []);

    if (!enabled || !visible) return null;

    return (
        <div
            data-testid="cookie-consent-banner"
            role="dialog"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-body"
            className="fixed z-[60] bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md"
        >
            <div className="bg-cream border-2 border-[var(--green)] rounded-2xl shadow-[0_6px_0_var(--green-dark)] p-5 sm:p-6">
                <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[var(--green)] flex items-center justify-center">
                        <Cookie size={18} className="text-cream" aria-hidden="true" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <h2
                            id="cookie-consent-title"
                            className="font-display text-sm sm:text-base text-[var(--green-dark)] leading-snug"
                        >
                            Wij gebruiken cookies
                        </h2>
                        <p
                            id="cookie-consent-body"
                            className="mt-1.5 text-xs sm:text-[13px] text-[#1f2937] leading-relaxed"
                        >
                            We gebruiken cookies om jouw ervaring te verbeteren en — alleen met jouw
                            toestemming — anonieme statistieken bij te houden. Lees meer in ons{' '}
                            <Link to="/cookies" className="underline hover:no-underline">cookiebeleid</Link>{' '}
                            en{' '}
                            <Link to="/privacy" className="underline hover:no-underline">privacybeleid</Link>.
                        </p>
                    </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
                    <button
                        type="button"
                        data-testid="cookie-consent-accept"
                        onClick={accept}
                        className="zz-btn zz-btn--primary !text-xs flex-1 min-h-[44px]"
                    >
                        <Check size={14} aria-hidden="true" /> Accepteren
                    </button>
                    <button
                        type="button"
                        data-testid="cookie-consent-decline"
                        onClick={decline}
                        className="zz-btn zz-btn--cream !text-xs flex-1 min-h-[44px]"
                    >
                        <X size={14} aria-hidden="true" /> Weigeren
                    </button>
                </div>
            </div>
        </div>
    );
}
